/**
 * THE BADGE — WHAT DECIDES IT EXISTS, AND WHAT IT IS CALLED.
 *
 * Two pure functions, kept away from the screen, because these are the two places
 * where a mistake would not show: a wrong delivery rule lets somebody in (or leaves
 * them out) with nothing on screen looking amiss, and a file name is only read once
 * the PDF sits in a downloads folder.
 */

/**
 * ═══ A BADGE IS A BUSINESS STATE, NOT A SWITCH ═══
 *
 * That is the prototype review's conclusion, and it holds here: the badge is only
 * issued once nothing is left to settle. Shown beside an unpaid registration, it
 * promises entry that the door will refuse — and the door is where they find out, in
 * front of everybody.
 *
 * ⚠️ THE ORDER OF THE CASES IS THE RULE. Somebody who declined AND owes money matches
 * two of them; asking them to pay for a badge they do not want would be the worse of
 * the two answers. So the reason CLOSEST to the person comes first.
 */
export type BadgeState =
  /** Nothing stands in the way: the card is shown. */
  | "ready"
  /** Nobody has answered the invitation yet — there is no registration. */
  | "no-answer"
  /** They said they are not coming. */
  | "declined"
  /** Somebody comes instead: the badge will be theirs, not this person's. */
  | "represented"
  /** They follow the event online: there is no door to show a badge at. */
  | "remote"
  /** A balance is still due. */
  | "unpaid";

/** The four participation answers, in the product's own vocabulary. */
const ANSWERS = ["onsite", "remote", "represented", "decline"] as const;
export type Answer = (typeof ANSWERS)[number];

/**
 * THE ANSWER, RECOGNISED RATHER THAN ASSUMED.
 *
 * ⚠️ NEEDED BECAUSE THE TWO SOURCES ARE NOT THE SAME TYPE. What the server holds is
 * already one of the four (`Recorded["answer"]`); what the participant has just
 * answered travels in the navigation state, where it is any STRING — it survives
 * reloads and nothing guarantees what is in there. An unknown value means "we do not
 * know", never "they are not coming".
 */
export function readAnswer(value: string | undefined): Answer | undefined {
  return ANSWERS.find((a) => a === value);
}

export function badgeState(situation: {
  /** `undefined` until the server has answered — never guessed. */
  responded?: boolean;
  answer?: Answer;
  /**
   * Is anything left to pay? `undefined` MEANS "we do not know", and that is a case of
   * its own: the balance query can fail (network, route in error), and locking on that
   * would withhold the badge of somebody who is in good standing, the day before the
   * event, with nothing they can do about it. Access control happens at the door and
   * does not depend on this screen.
   */
  unpaid?: boolean;
}): BadgeState {
  if (situation.responded !== true) return "no-answer";
  if (situation.answer === "decline") return "declined";
  if (situation.answer === "represented") return "represented";
  /* ⚠️ À DISTANCE, PAS DE BADGE, et AVANT le solde (tâche #225) : le bloqueur « après
     paiement » promettrait un badge qui ne viendra jamais. La V1 ne montre ni QR ni
     téléchargement à un inscrit à distance (`!UserEvent.isRemote`). */
  if (situation.answer === "remote") return "remote";
  if (situation.unpaid === true) return "unpaid";
  return "ready";
}

/**
 * ⚠️ WINDOWS AND MACOS RESERVED CHARACTERS — those only. Accented letters travel
 * everywhere, and dropping them would render "Bâle" as "Ble".
 */
const RESERVED = '\\/:*?"<>|';

function part(value: string | undefined): string {
  return [...(value ?? "")]
    .filter((c) => {
      /**
       * ⚠️ FILTERED BY CODE POINT, NOT BY REGULAR EXPRESSION. A hand-written control
       * character class reads badly and is one byte away from being wrong; here the
       * condition says exactly what it drops.
       */
      const code = c.codePointAt(0) ?? 0;
      return code > 31 && code !== 127 && !RESERVED.includes(c);
    })
    .join("")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * THE FILE NAME: "Surname-Firstname-CODE-Event.pdf".
 *
 * ⚠️ IT IS FORCED CLIENT-SIDE, AND THERE IS NO OTHER WAY. The server returns the PDF
 * *inline*; the front turns it into a blob URL, and a blob URL CANNOT carry a file
 * name. The server's `Content-Disposition` is dead weight: the two applications sit on
 * different domains and the gateway does not expose it. Hence the `download` attribute
 * of an `<a>` — see `downloadBadge`.
 *
 * The rule is V1's (`fo-meeting/src/utils/badgeFile.js`), character for character: the
 * two fronts coexist, and two naming conventions for the same document would hand out
 * two visibly different files for one badge.
 */
export function badgeFileName(holder: {
  lastName?: string;
  firstName?: string;
  code?: string;
  eventName?: string;
}): string {
  const base = [holder.lastName, holder.firstName, holder.code, holder.eventName]
    .map(part)
    .filter(Boolean)
    .join("-");
  // An account with no name exists (created on the fly, half imported): a generic name
  // beats a file called ".pdf", which some systems treat as hidden.
  return `${base || "badge"}.pdf`;
}
